import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { BookOpen, ArrowRight, Clock } from "lucide-react";

// -----------------------------------------------------------------------------
// ArticlesOfInterest — a reusable strip that renders on every category /
// vertical / hub page. Pulls articles from /api/sp-articles/by-tag/{tag}
// so a single article tagged e.g. "health" appears wherever any Health
// page places <ArticlesOfInterest tags={["health"]} />.
//
// Props:
//   tags         list of tags to try in order — first non-empty wins
//   title        section heading (defaults to "Articles of Interest")
//   subtitle     optional line under the heading
//   limit        max cards to show (default 3)
//   accent       CSS colour for headings / underlines
//   testidPrefix data-testid prefix
//
// The component silently renders nothing if the API errors or returns no
// items — a category page doesn't need to know whether an article of
// interest exists yet.
// -----------------------------------------------------------------------------

export default function ArticlesOfInterest({
  tags = [],
  title = "Articles of Interest",
  subtitle = "Editorial reads picked to complement this section.",
  limit = 3,
  accent = "#7C3AED",
  testidPrefix = "articles-of-interest",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Try each tag in order; first tag with items wins.
        for (const t of tags) {
          if (!t) continue;
          try {
            const { data } = await http.get(`/sp-articles/by-tag/${encodeURIComponent(t)}`, {
              params: { limit, offset: 0 },
            });
            const list = Array.isArray(data?.items) ? data.items : [];
            if (list.length > 0) {
              if (!cancelled) setItems(list.slice(0, limit));
              return;
            }
          } catch {
            // try next tag
          }
        }
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tags.join(","), limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="mt-16"
      data-testid={testidPrefix}
      aria-labelledby={`${testidPrefix}-heading`}
    >
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div
            className="overline mb-1 inline-flex items-center gap-1.5"
            style={{ color: accent }}
          >
            <BookOpen className="w-3.5 h-3.5" />
            EDITORIAL
          </div>
          <h2
            id={`${testidPrefix}-heading`}
            className="font-display text-2xl md:text-3xl tracking-tight"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              {subtitle}
            </p>
          )}
        </div>
        <Link
          to="/articles"
          data-testid={`${testidPrefix}-see-all`}
          className="text-sm font-medium inline-flex items-center gap-1 hover:underline"
          style={{ color: accent }}
        >
          All articles <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="wofy-card animate-pulse">
              <div className="aspect-[16/10] rounded-xl bg-neutral-100 mb-3" />
              <div className="h-4 rounded bg-neutral-100 w-3/4 mb-2" />
              <div className="h-3 rounded bg-neutral-100 w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <Link
              key={a.slug || a.id}
              to={`/articles/${a.slug || a.id}`}
              data-testid={`${testidPrefix}-card-${a.slug || a.id}`}
              className="wofy-card block hover:shadow-md transition-shadow overflow-hidden group"
            >
              {a.feature_image ? (
                <img
                  src={a.feature_image}
                  alt={a.title || "Article cover"}
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover rounded-xl mb-3 group-hover:scale-[1.02] transition-transform duration-500"
                />
              ) : (
                <div
                  className="w-full aspect-[16/10] rounded-xl mb-3 flex items-center justify-center"
                  style={{ background: `${accent}12`, color: accent }}
                >
                  <BookOpen className="w-8 h-8 opacity-50" />
                </div>
              )}
              <div className="font-display text-lg leading-snug line-clamp-2">
                {a.title}
              </div>
              {a.excerpt && (
                <p className="mt-2 text-sm leading-snug line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
                  {a.excerpt}
                </p>
              )}
              <div className="mt-3 flex items-center gap-3 text-[11px] font-mono uppercase tracking-wide opacity-70">
                {a.author && <span>{a.author}</span>}
                {a.reading_minutes && (
                  <span className="inline-flex items-center gap-0.5">
                    <Clock className="w-3 h-3" /> {a.reading_minutes} min
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
